// Selection mode for the Risk Areas page's bulk bar. In Redux because it's read
// by the pinned bar, the row list and both bulk writes at once.
//
// `selectedCards` is a Record<id, true>, not a Set: a Set in the store is not
// serializable and hands out a fresh reference on every read, defeating both
// the DevTools timeline and any memoized selector over it.
//
// Every reducer here is a named intent rather than a setter, so no caller can
// leave the four fields inconsistent.
import { createSlice, PayloadAction } from "@reduxjs/toolkit";

export interface RiskAreaSelectionState {
  selectionMode: boolean;
  selectedCards: Record<number, true>;
  allResultsSelected: boolean;
  showDeleteConfirm: boolean;
}

const initialState: RiskAreaSelectionState = {
  selectionMode: false,
  selectedCards: {},
  allResultsSelected: false,
  showDeleteConfirm: false,
};

const riskAreaSelectionSlice = createSlice({
  name: "riskAreaSelection",
  initialState,
  reducers: {
    // The toolbar's Bulk Update shortcut. Ticking any row checkbox opens the bar
    // too — the shortcut is not a prerequisite.
    selectionModeStarted(state) {
      state.selectionMode = true;
    },

    // Leave selection mode and drop every selection. Also what the page
    // dispatches on unmount, so re-entering never inherits a stale selection.
    selectionModeExited() {
      return initialState;
    },

    // Tick / untick one row. Selection is one set across every section, so the
    // user can keep scrolling and ticking rows in other accordions.
    // canEnterSelectionMode carries the caller's permission check: without it a
    // tick still selects, it just can't open the bulk bar.
    cardToggled(
      state,
      action: PayloadAction<{ id: number; canEnterSelectionMode: boolean }>
    ) {
      const { id, canEnterSelectionMode } = action.payload;
      if (state.selectedCards[id]) {
        delete state.selectedCards[id];
        state.allResultsSelected = false
      } else {
        state.selectedCards[id] = true;
      }
      const count = Object.keys(state.selectedCards).length;
      if (count === 0) state.allResultsSelected = false;
      if (count > 0 && canEnterSelectionMode) state.selectionMode = true;
    },

    // Drop every selection but stay in selection mode — the checkbox being
    // unticked and the banner's "Clear selection" link.
    selectionCleared(state) {
      state.selectedCards = {};
      state.allResultsSelected = false;
    },

    // Widen from the ticked rows to every result behind the active tab. Clears
    // selectedCards because the flag alone now drives every row's checked
    // state — see RiskSections' `allResultsSelected || selectedCards.has(id)`.
    allResultsSelectedToggled(state) {
      state.allResultsSelected = true;
      state.selectedCards = {};
    },

    // A row was unticked while every result was selected. Every rendered row
    // shows checked in that mode, so the click can only mean "deselect this
    // one and fall back to manual mode" — the new selection is every row
    // currently on screen minus this one.
    deselectedFromAll(
      state,
      action: PayloadAction<{ deselectedId: number; loadedIds: number[] }>
    ) {
      const { deselectedId, loadedIds } = action.payload;
      state.allResultsSelected = false;
      state.selectedCards = {};
      loadedIds.forEach((id) => {
        if (id !== deselectedId) state.selectedCards[id] = true;
      });
    },

    deleteRequested(state) {
      state.showDeleteConfirm = true;
    },

    deleteCancelled(state) {
      state.showDeleteConfirm = false;
    },
  },
});

export const {
  selectionModeStarted,
  selectionModeExited,
  cardToggled,
  selectionCleared,
  allResultsSelectedToggled,
  deselectedFromAll,
  deleteRequested,
  deleteCancelled,
} = riskAreaSelectionSlice.actions;

export const selectRiskAreaSelection = (state: any): RiskAreaSelectionState =>
  state.riskAreaSelection;

export const riskAreaSelectionReducer = riskAreaSelectionSlice.reducer;
